/* ==========================================================================
   Bundle PDP — "One Bundle, One Finished Look" step cards.

   Three jobs, all progressive enhancement over markup that already works
   with JavaScript off:

   * the progress bar under the track — width and offset mirror the native
     scrollbar proportions, and it stays hidden unless the track really
     overflows, so a section whose cards all fit never shows a dead rail;
   * drag-to-scroll for mouse users, who get no swipe and no arrows here;
   * the CTA. It is a real link to the setting's fallback URL, and only when
     the bundle hero is actually on the page does this take over: if the
     shopper has already made every choice the hero needs, it takes them
     straight to the hero's add-to-cart button, and if they have not, it
     opens the hero's shade picker and lands them on the first swatch.
   ========================================================================== */
(function () {
  'use strict';

  var DRAG_THRESHOLD = 4;

  /* ------------------------------------------------------------ progress -- */

  function setProgress(viewport, track, thumb) {
    var scrollable = track.scrollWidth - track.clientWidth;
    var overflows = scrollable > 1;

    viewport.dataset.overflow = overflows ? 'true' : 'false';
    if (!overflows || !thumb) return;

    var ratio = track.clientWidth / track.scrollWidth;
    var progress = track.scrollLeft / scrollable;

    // The thumb travels the rail's leftover length, so it lands flush with
    // each end instead of overshooting.
    thumb.style.setProperty('--lk-thumb-w', (ratio * 100).toFixed(3) + '%');
    thumb.style.setProperty('--lk-thumb-x', (progress * (1 - ratio) * 100).toFixed(3) + '%');
  }

  /* ---------------------------------------------------------------- drag -- */

  function snapToNearest(track) {
    // Measured live off the rendered boxes rather than reconstructed from
    // offsetLeft: the anchor is wherever a snapped card should sit, which is
    // the scrollport's left edge plus the track's own gutter, and every card
    // is compared against it in the same viewport coordinates.
    var pad = parseFloat(window.getComputedStyle(track).paddingLeft) || 0;
    var anchor = track.getBoundingClientRect().left + pad;
    var closest = 0;
    var shortest = Infinity;

    track.querySelectorAll('.bundle-lk__card').forEach(function (card) {
      var delta = card.getBoundingClientRect().left - anchor;
      if (Math.abs(delta) < shortest) {
        shortest = Math.abs(delta);
        closest = delta;
      }
    });

    if (shortest === Infinity || shortest < 1) return;

    // scrollTo clamps on its own, so the first and last cards simply come to
    // rest against their ends instead of overshooting.
    track.scrollTo({ left: track.scrollLeft + closest, behavior: 'smooth' });
  }

  function enableDrag(track) {
    // Coarse pointers already swipe; hijacking them would fight the
    // browser's own momentum and snapping.
    if (!window.matchMedia('(hover: hover) and (pointer: fine)').matches) return;

    var startX = 0;
    var startScroll = 0;
    var pointerId = null;
    var moved = false;

    track.addEventListener('pointerdown', function (event) {
      if (event.button !== 0 || pointerId !== null) return;
      pointerId = event.pointerId;
      startX = event.clientX;
      startScroll = track.scrollLeft;
      moved = false;
    });

    track.addEventListener('pointermove', function (event) {
      if (event.pointerId !== pointerId) return;

      var delta = event.clientX - startX;
      if (!moved && Math.abs(delta) < DRAG_THRESHOLD) return;

      if (!moved) {
        moved = true;
        track.dataset.dragging = 'true';
        try {
          track.setPointerCapture(pointerId);
        } catch (error) {
          /* capture is a nicety; the move handler still fires without it */
        }
      }

      track.scrollLeft = startScroll - delta;
      event.preventDefault();
    });

    function release(event) {
      if (event.pointerId !== pointerId) return;
      pointerId = null;
      if (!moved) return;

      // Snapping is off during the drag so the track can follow the cursor
      // freely. Re-snapping on a property change is not dependable across
      // browsers, so the nearest card is scrolled to explicitly instead.
      delete track.dataset.dragging;
      snapToNearest(track);
    }

    track.addEventListener('pointerup', release);
    track.addEventListener('pointercancel', release);

    // A drag that ends over a card would otherwise fire that card's click.
    track.addEventListener('click', function (event) {
      if (moved) {
        event.preventDefault();
        event.stopPropagation();
        moved = false;
      }
    }, true);
  }

  /* ----------------------------------------------------------------- cta -- */

  function prefersReducedMotion() {
    return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  }

  function scrollTo(node) {
    if (!node || !node.scrollIntoView) return;
    node.scrollIntoView({
      behavior: prefersReducedMotion() ? 'auto' : 'smooth',
      block: 'center'
    });
  }

  /* The picker used to live inside the hero, so the first `[data-bundle-hero]`
     root was always the right target. It now has its own section, and the hero
     no longer contains it — so pick the root that actually holds a shade
     picker, and only fall back to the first one if none does. */
  function findPicker() {
    var roots = Array.prototype.slice.call(document.querySelectorAll('[data-bundle-hero]'));
    return roots.filter(function (root) {
      return root.querySelector('[data-bh-shade]');
    })[0] || roots[0] || null;
  }

  function initCta(root) {
    var cta = root.querySelector('[data-lk-cta]');
    if (!cta) return;

    cta.addEventListener('click', function (event) {
      var picker = findPicker();

      // No picker on this page: leave the link alone and let it navigate to
      // whatever the setting points at.
      if (!picker) return;

      var submit = picker.querySelector('[data-bh-submit]');
      var ready = !!submit && submit.getAttribute('aria-disabled') === 'false';

      event.preventDefault();

      if (ready) {
        // Every choice is already made — go straight to add-to-cart. Focus
        // moves too, so a keyboard shopper carries on from the same place.
        scrollTo(submit);
        if (submit.focus) {
          window.setTimeout(function () {
            submit.focus({ preventScroll: true });
          }, prefersReducedMotion() ? 0 : 500);
        }
        return;
      }

      // Something is still unchosen. Where the picker is still collapsed
      // inside a hero, its reveal button expands it and scrolls to the first
      // swatch — defer to that rather than duplicating the behaviour. With
      // the picker in its own section there is no reveal button, and the
      // scroll below is the whole job.
      var reveal = picker.querySelector('[data-bh-reveal]');
      if (reveal) {
        reveal.click();
        return;
      }

      scrollTo(picker.querySelector('[data-bh-swatch]') || picker.querySelector('[data-bh-shade]') || picker);
    });
  }

  /* ---------------------------------------------------------------- boot -- */

  function startViewport(viewport) {
    var track = viewport.querySelector('[data-lk-track]');
    if (!track) return;

    var thumb = viewport.querySelector('[data-lk-thumb]');
    var queued = false;

    function update() {
      queued = false;
      setProgress(viewport, track, thumb);
    }

    function schedule() {
      if (queued) return;
      queued = true;
      window.requestAnimationFrame(update);
    }

    track.addEventListener('scroll', schedule, { passive: true });
    window.addEventListener('resize', schedule);

    if (typeof ResizeObserver === 'function') {
      new ResizeObserver(schedule).observe(track);
    }

    // Late-loading card images change scrollWidth after the first measure.
    track.querySelectorAll('img').forEach(function (img) {
      if (!img.complete) img.addEventListener('load', schedule, { once: true });
    });

    enableDrag(track);
    update();
  }

  function init(scope) {
    (scope || document).querySelectorAll('[data-bundle-finished-look]').forEach(function (root) {
      if (root.dataset.lkReady === 'true') return;
      root.dataset.lkReady = 'true';

      var viewport = root.querySelector('[data-lk-viewport]');
      if (viewport) startViewport(viewport);
      initCta(root);
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { init(); });
  } else {
    init();
  }

  document.addEventListener('shopify:section:load', function (event) { init(event.target); });
})();
